/**
 * Tiny WebAudio beep utilities for ticket verification feedback.
 * No audio asset files are needed — tones are synthesised on the fly.
 *
 * Guards gracefully when AudioContext is not available (jsdom / tests).
 */

function playTone(
  frequency: number,
  duration: number,
  gainValue: number,
  type: OscillatorType = 'sine'
): void {
  try {
    const AudioCtx =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : typeof (window as Window & { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext !== 'undefined'
          ? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
          : null

    if (!AudioCtx) return

    const ctx = new AudioCtx()
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime)

    gainNode.gain.setValueAtTime(gainValue, ctx.currentTime)
    // Fade out to avoid click
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration)

    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + duration)

    // Close context after playback to free resources
    oscillator.onended = () => {
      ctx.close().catch(() => undefined)
    }
  } catch {
    // Fail silently — audio is non-critical
  }
}

/** Play a positive two-tone "approved" chime (ascending). */
export function playApproved(): void {
  playTone(880, 0.15, 0.4, 'sine')
  setTimeout(() => playTone(1100, 0.25, 0.3, 'sine'), 120)
}

/** Play a negative low buzz "rejected" tone. */
export function playRejected(): void {
  playTone(220, 0.3, 0.35, 'square')
}
